import { useEffect, useState } from "react";
import { trpc } from "../../trpc";
import { Merchant } from "../../types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

// Events the El Castillo / Grupo R webhook fired on, pre-selected for new setups.
const DEFAULT_GRUPOR_EVENTS = [
  "lead.redirected-to-human",
  "lead.appointment-created",
  "lead.inactivated",
];

type Props = {
  merchant: Merchant | null;
};

const InternalCrmTab = ({ merchant }: Props) => {
  const merchantId = merchant?.id ?? "";
  const isRentals = merchant?.type === "rentals";

  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.crm.getInternal.useQuery(
    { merchantId },
    { enabled: !!merchantId && isRentals }
  );

  const [handler, setHandler] = useState<string>("");
  const [events, setEvents] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Re-initialize when switching merchants.
  useEffect(() => {
    setInitialized(false);
  }, [merchantId]);

  useEffect(() => {
    if (!data || initialized) return;
    if (data.configuration) {
      setHandler(data.configuration.handler ?? data.handlerKeys[0] ?? "");
      setEvents(data.configuration.events ?? []);
    } else {
      setHandler(data.handlerKeys[0] ?? "");
      setEvents(data.availableEvents.filter((e) => DEFAULT_GRUPOR_EVENTS.includes(e)));
    }
    setInitialized(true);
  }, [data, initialized]);

  const setupMutation = trpc.crm.setupInternal.useMutation({
    onSuccess: () => {
      toast.success("CRM interno configurado");
      utils.crm.getInternal.invalidate({ merchantId });
    },
    onError: (error) =>
      toast.error("Error al configurar CRM interno", { description: error.message }),
  });

  const toggleEvent = (event: string) => {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  const handleSave = () => {
    if (!merchantId || !handler || events.length === 0) return;
    setupMutation.mutate({ merchantId, handler, events });
  };

  if (!merchant) return null;

  if (!isRentals) {
    return (
      <Alert status="warning">
        El proveedor "internal" solo está soportado para merchants de tipo rentals.
      </Alert>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="w-6 h-6" />
      </div>
    );
  }

  const isConfigured = !!data.configuration && !!data.connection?.enabled;

  return (
    <div className="space-y-4">
      <div className="rounded-md border p-3 text-sm">
        <p className="font-medium">Estado</p>
        <p className="text-muted-foreground">
          {isConfigured ? "Configurado y activo" : "No configurado"}
          {data.connection
            ? ` · conexión: ${data.connection.enabled ? "habilitada" : "deshabilitada"} (${
                data.connection.status ?? "—"
              })`
            : " · sin conexión"}
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="internalHandler">Handler</Label>
        <Select value={handler} onValueChange={setHandler}>
          <SelectTrigger id="internalHandler">
            <SelectValue placeholder="Selecciona un handler" />
          </SelectTrigger>
          <SelectContent>
            {data.handlerKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Eventos que disparan la creación</Label>
        <div className="grid grid-cols-1 gap-2">
          {data.availableEvents.map((event) => (
            <label key={event} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={events.includes(event)}
                onCheckedChange={() => toggleEvent(event)}
              />
              {event}
            </label>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSave}
        disabled={setupMutation.isLoading || !handler || events.length === 0}
      >
        {setupMutation.isLoading ? "Guardando..." : "Guardar CRM interno"}
      </Button>

      <p className="text-xs text-muted-foreground">
        Las credenciales de Grupo R se siguen leyendo del Webhook del merchant. Esto solo
        configura el disparador por eventos del nuevo proveedor "internal".
      </p>
    </div>
  );
};

export default InternalCrmTab;
